import React, { useState } from "react";
import "./SupplierDashboard.css";

const SupplierDashboard = () => {
  // Get current month start and end dates
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  const startOfMonth = new Date(currentYear, currentMonth, 1);
  const endOfMonth = new Date(currentYear, currentMonth + 1, 0);

  const formatDate = (date) => {
    return date.toISOString().split("T")[0];
  };

  // State for filters
  const [filters, setFilters] = useState({
    fromDate: formatDate(startOfMonth),
    toDate: formatDate(endOfMonth),
    company: "all",
  });

  // State for mobile sidebar
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Sample company data for pie chart
  const companyData = [
    { name: "Tech Solutions", requests: 42, color: "#4299e1" },
    { name: "Global Edu", requests: 38, color: "#48bb78" },
    { name: "Sunshine Travels", requests: 28, color: "#ed8936" },
    { name: "Corporate Group", requests: 24, color: "#9f7aea" },
    { name: "Innovate IT", requests: 18, color: "#ed64a6" },
    { name: "Others", requests: 32, color: "#a0aec0" },
  ];

  // Holiday packages data
  const holidayPackages = [
    {
      name: "Europe Tour Package",
      type: "premium",
      duration: "14 Days",
      price: 2499,
      status: "active",
      bookings: 45,
    },
    {
      name: "Asian Adventure",
      type: "standard",
      duration: "10 Days",
      price: 1299,
      status: "active",
      bookings: 28,
    },
    {
      name: "USA East Coast",
      type: "express",
      duration: "7 Days",
      price: 899,
      status: "active",
      bookings: 32,
    },
    {
      name: "Dubai Luxury",
      type: "premium",
      duration: "5 Days",
      price: 1999,
      status: "inactive",
      bookings: 0,
    },
  ];

  // Calculate counts
  const visaRequestCount = 142;
  const appointmentSlotCount = 87; // Available slots count
  const holidayPackageCount = 12;
  const promotionCount = 8;
  const totalRevenue = "₹4,57,000";

  // Stats data
  const statsData = [
    {
      title: "Visa Requests",
      value: visaRequestCount.toString(),
      icon: "📄",
      color: "supplierdashboard-stat-card-blue",
    },
    {
      title: "Appointment Slots",
      value: appointmentSlotCount.toString(),
      icon: "📅",
      color: "supplierdashboard-stat-card-green",
    },
    {
      title: "Holiday Packages",
      value: holidayPackageCount.toString(),
      icon: "🎁",
      color: "supplierdashboard-stat-card-amber",
    },
    {
      title: "Promotions",
      value: promotionCount.toString(),
      icon: "📢",
      color: "supplierdashboard-stat-card-purple",
    },
    {
      title: "Total Revenue",
      value: totalRevenue,
      icon: "💰",
      color: "supplierdashboard-stat-card-teal",
    },
  ];

  // Visa requests data
  const visaRequests = [
    {
      id: "VR-2023-001",
      applicant: "John Smith",
      type: "Tourist Visa",
      date: "Nov 15, 2023",
      status: "pending",
      priority: "medium",
    },
    {
      id: "VR-2023-002",
      applicant: "Maria Garcia",
      type: "Business Visa",
      date: "Nov 14, 2023",
      status: "approved",
      priority: "high",
    },
    {
      id: "VR-2023-003",
      applicant: "Ahmed Khan",
      type: "Student Visa",
      date: "Nov 13, 2023",
      status: "rejected",
      priority: "medium",
    },
    {
      id: "VR-2023-004",
      applicant: "Emma Wilson",
      type: "Work Visa",
      date: "Nov 12, 2023",
      status: "approved",
      priority: "low",
    },
  ];

  const appointments = [
    {
      id: "APT-001",
      applicant: "Robert Chen",
      date: "Nov 20, 2023",
      time: "10:30 AM",
      type: "submission",
      status: "scheduled",
    },
    {
      id: "APT-002",
      applicant: "Sophia Williams",
      date: "Nov 22, 2023",
      time: "02:00 PM",
      type: "interview",
      status: "scheduled",
    },
    {
      id: "APT-003",
      applicant: "David Miller",
      date: "Nov 25, 2023",
      time: "11:15 AM",
      type: "submission",
      status: "completed",
    },
  ];

  const promotions = [
    {
      name: "Early Bird Special",
      discount: "20% OFF",
      validFrom: "Nov 1, 2023",
      validTo: "Dec 15, 2023",
      usage: "45/100 used",
      status: "active",
    },
    {
      name: "Family Package Deal",
      discount: "15% OFF",
      validFrom: "Oct 15, 2023",
      validTo: "Nov 30, 2023",
      usage: "22/50 used",
      status: "active",
    },
    {
      name: "Corporate Discount",
      discount: "25% OFF",
      validFrom: "Sep 1, 2023",
      validTo: "Oct 31, 2023",
      usage: "68/100 used",
      status: "expired",
    },
  ];

  const navItems = [
    { icon: "📊", label: "Dashboard", active: true },
    { icon: "📄", label: "Visa Requests" },
    { icon: "📅", label: "Appointments" },
    { icon: "🎁", label: "Holiday Packages" },
    { icon: "📢", label: "Promotions" },
    { icon: "🏢", label: "Companies" },
    { icon: "📈", label: "Reports" },
    { icon: "⚙️", label: "Settings" },
  ];

  // Calculate pie chart data
  const totalRequests = companyData.reduce(
    (sum, company) => sum + company.requests,
    0
  );
  let cumulativeAngle = 0;

  // Handler functions
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      fromDate: formatDate(startOfMonth),
      toDate: formatDate(endOfMonth),
      company: "all",
    });
  };

  // Helper functions
  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "supplierdashboard-status-approved";
      case "rejected":
        return "supplierdashboard-status-rejected";
      case "scheduled":
        return "supplierdashboard-status-scheduled";
      case "completed":
        return "supplierdashboard-status-completed";
      default:
        return "supplierdashboard-status-pending";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return "✅";
      case "rejected":
        return "❌";
      case "scheduled":
        return "⏰";
      case "completed":
        return "✓";
      default:
        return "⏳";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "supplierdashboard-priority-high";
      case "medium":
        return "supplierdashboard-priority-medium";
      default:
        return "supplierdashboard-priority-low";
    }
  };

  const getPackageStatusClass = (status) => {
    return status === "active"
      ? "supplierdashboard-package-active"
      : "supplierdashboard-package-inactive";
  };

  const getPackageTypeIcon = (type) => {
    switch (type) {
      case "premium":
        return "⭐";
      case "express":
        return "⚡";
      default:
        return "🏨";
    }
  };

  return (
    <div className="supplierdashboard">
      {/* Mobile Header */}
      <div className="supplierdashboard-mobile-header">
        <button
          className="supplierdashboard-menu-toggle"
          onClick={() => setSidebarOpen(true)}
        >
          ☰
        </button>
        <div className="supplierdashboard-mobile-user-info">
          <div className="supplierdashboard-user-avatar-small">SA</div>
          <div>
            <p className="supplierdashboard-user-name">Supplier Admin</p>
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <aside
        className={`supplierdashboard-sidebar ${
          sidebarOpen ? "supplierdashboard-sidebar-open" : ""
        }`}
      >
        <div className="supplierdashboard-sidebar-header">
          <div className="supplierdashboard-logo-container">
            <div className="supplierdashboard-logo-icon">🛂</div>
            <div>
              <h1 className="supplierdashboard-logo-text">VisaPro</h1>
              <p className="supplierdashboard-logo-subtext">
                Supplier Dashboard
              </p>
            </div>
          </div>
          <button
            className="supplierdashboard-sidebar-close"
            onClick={() => setSidebarOpen(false)}
          >
            ×
          </button>
        </div>

        <nav className="supplierdashboard-sidebar-nav">
          {navItems.map((item, index) => (
            <button
              key={index}
              className={`supplierdashboard-nav-item ${
                item.active ? "supplierdashboard-nav-item-active" : ""
              }`}
            >
              <span className="supplierdashboard-nav-icon">{item.icon}</span>
              <span className="supplierdashboard-nav-label">{item.label}</span>
              {item.active && (
                <div className="supplierdashboard-nav-indicator" />
              )}
            </button>
          ))}
        </nav>

        <div className="supplierdashboard-sidebar-footer">
          <div className="supplierdashboard-user-info">
            <div className="supplierdashboard-user-avatar">SA</div>
            <div>
              <p className="supplierdashboard-user-name">Supplier Admin</p>
              <p className="supplierdashboard-user-status">Online</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="supplierdashboard-sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="supplierdashboard-main-content">
        {/* Header */}
        <header className="supplierdashboard-main-header">
          <div className="supplierdashboard-header-content">
            <div className="supplierdashboard-header-title">
              <h1>Supplier Dashboard</h1>
              <p>Manage visa requests, appointments, and holiday packages</p>
            </div>

            <div className="supplierdashboard-header-actions">
              <div className="supplierdashboard-company-filter">
                <select
                  value={filters.company}
                  onChange={(e) =>
                    handleFilterChange("company", e.target.value)
                  }
                  className="supplierdashboard-company-select"
                >
                  <option value="all">All Companies</option>
                  <option value="tech">Tech Solutions</option>
                  <option value="global">Global Education</option>
                  <option value="sunshine">Sunshine Travels</option>
                </select>
              </div>

              <button className="supplierdashboard-notification-btn">
                <span className="supplierdashboard-notification-icon">🔔</span>
                <span className="supplierdashboard-notification-badge" />
              </button>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <div className="supplierdashboard-content-wrapper">
          {/* Filter Section */}
          <div className="supplierdashboard-filter-section">
            <div className="supplierdashboard-filter-grid">
              <div className="supplierdashboard-filter-group">
                <label>From Date</label>
                <input
                  type="date"
                  value={filters.fromDate}
                  onChange={(e) =>
                    handleFilterChange("fromDate", e.target.value)
                  }
                  className="supplierdashboard-filter-input"
                />
              </div>

              <div className="supplierdashboard-filter-group">
                <label>To Date</label>
                <input
                  type="date"
                  value={filters.toDate}
                  onChange={(e) => handleFilterChange("toDate", e.target.value)}
                  className="supplierdashboard-filter-input"
                />
              </div>

              <div className="supplierdashboard-filter-actions">
                <button
                  className="supplierdashboard-filter-reset-btn"
                  onClick={handleResetFilters}
                >
                  <span className="supplierdashboard-reset-icon">🔄</span>
                  Reset Dates
                </button>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="supplierdashboard-stats-grid">
            {statsData.map((stat, index) => (
              <div
                key={index}
                className={`supplierdashboard-stat-card ${stat.color}`}
              >
                <div className="supplierdashboard-stat-header">
                  <div className="supplierdashboard-stat-icon-container">
                    <span className="supplierdashboard-stat-icon">
                      {stat.icon}
                    </span>
                  </div>
                </div>

                <h4 className="supplierdashboard-stat-value">{stat.value}</h4>
                <p className="supplierdashboard-stat-title">{stat.title}</p>

                <div className="supplierdashboard-stat-progress">
                  <div className="supplierdashboard-progress-bar" />
                </div>
              </div>
            ))}
          </div>

          {/* Pie Chart and Package Status */}
          <div className="supplierdashboard-content-grid">
            {/* Company Performance Pie Chart */}
            <div className="supplierdashboard-content-card">
              <div className="supplierdashboard-card-header">
                <div className="supplierdashboard-card-title">
                  <div className="supplierdashboard-card-icon supplierdashboard-chart-icon">
                    📊
                  </div>
                  <div>
                    <h3>Company Performance</h3>
                    <p>Visa requests distribution by company</p>
                  </div>
                </div>
              </div>

              <div className="supplierdashboard-pie-chart-container">
                <div className="supplierdashboard-pie-chart">
                  {companyData.map((company, index) => {
                    const percentage = (company.requests / totalRequests) * 100;
                    const angle = (percentage / 100) * 360;
                    const startAngle = cumulativeAngle;
                    cumulativeAngle += angle;

                    return (
                      <div
                        key={index}
                        className="supplierdashboard-pie-segment"
                        style={{
                          backgroundColor: company.color,
                          clipPath: `conic-gradient(${company.color} 0deg ${angle}deg, transparent ${angle}deg 360deg)`,
                          transform: `rotate(${startAngle}deg)`,
                        }}
                      />
                    );
                  })}
                  <div className="supplierdashboard-pie-center">
                    <span className="supplierdashboard-pie-total">
                      {totalRequests}
                    </span>
                    <span className="supplierdashboard-pie-label">
                      Total Requests
                    </span>
                  </div>
                </div>

                <div className="supplierdashboard-pie-legend">
                  {companyData.map((company, index) => (
                    <div key={index} className="supplierdashboard-legend-item">
                      <div
                        className="supplierdashboard-legend-color"
                        style={{ backgroundColor: company.color }}
                      />
                      <span className="supplierdashboard-legend-name">
                        {company.name}
                      </span>
                      <span className="supplierdashboard-legend-value">
                        {company.requests} (
                        {Math.round((company.requests / totalRequests) * 100)}%)
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Holiday Packages */}
            <div className="supplierdashboard-content-card">
              <div className="supplierdashboard-card-header">
                <div className="supplierdashboard-card-title">
                  <div className="supplierdashboard-card-icon supplierdashboard-package-icon">
                    🎁
                  </div>
                  <div>
                    <h3>Holiday Packages</h3>
                    <p>Active holiday packages</p>
                  </div>
                </div>
              </div>

              <div className="supplierdashboard-package-status">
                <div className="supplierdashboard-packages-list">
                  {holidayPackages.map((pkg, index) => (
                    <div key={index} className="supplierdashboard-package-item">
                      <div className="supplierdashboard-package-item-header">
                        <div className="supplierdashboard-package-icon-small">
                          {getPackageTypeIcon(pkg.type)}
                        </div>
                        <div className="supplierdashboard-package-item-info">
                          <h4 className="supplierdashboard-package-name">
                            {pkg.name}
                          </h4>
                          <span className="supplierdashboard-package-duration">
                            {pkg.duration}
                          </span>
                        </div>
                        <span
                          className={`supplierdashboard-package-status-badge ${getPackageStatusClass(
                            pkg.status
                          )}`}
                        >
                          {pkg.status === "active" ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <div className="supplierdashboard-package-item-details">
                        <div className="supplierdashboard-package-price">
                          <span className="supplierdashboard-price-label">
                            Price:
                          </span>
                          <span className="supplierdashboard-price-value">
                            ₹{pkg.price}
                          </span>
                        </div>
                        <div className="supplierdashboard-package-bookings">
                          <span className="supplierdashboard-bookings-label">
                            Bookings:
                          </span>
                          <span className="supplierdashboard-bookings-value">
                            {pkg.bookings}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Visa Requests & Appointments */}
          <div className="supplierdashboard-content-grid">
            {/* Visa Requests */}
            <div className="supplierdashboard-content-card">
              <div className="supplierdashboard-card-header">
                <div className="supplierdashboard-card-title">
                  <div className="supplierdashboard-card-icon supplierdashboard-visa-icon">
                    📄
                  </div>
                  <div>
                    <h3>Recent Visa Requests</h3>
                    <p>Latest visa applications</p>
                  </div>
                </div>
                <button className="supplierdashboard-view-all-btn">
                  View All →
                </button>
              </div>

              <div className="supplierdashboard-card-content">
                {visaRequests.map((request, index) => (
                  <div key={index} className="supplierdashboard-data-row">
                    <div className="supplierdashboard-row-content">
                      <div className="supplierdashboard-row-icon">
                        <div className="supplierdashboard-icon-container">
                          <span className="supplierdashboard-request-icon">
                            📋
                          </span>
                          <div
                            className={`supplierdashboard-priority-dot ${getPriorityColor(
                              request.priority
                            )}`}
                          />
                        </div>
                      </div>
                      <div className="supplierdashboard-row-details">
                        <div className="supplierdashboard-row-header">
                          <h4>{request.id}</h4>
                          <span
                            className={`supplierdashboard-status-badge ${getStatusColor(
                              request.status
                            )}`}
                          >
                            <span className="supplierdashboard-status-icon">
                              {getStatusIcon(request.status)}
                            </span>
                            <span>{request.status}</span>
                          </span>
                        </div>
                        <p className="supplierdashboard-row-applicant">
                          {request.applicant}
                        </p>
                        <p className="supplierdashboard-row-meta">
                          {request.type} • {request.date}
                        </p>
                      </div>
                    </div>

                    <div className="supplierdashboard-row-actions">
                      <button className="supplierdashboard-action-btn supplierdashboard-view-btn">
                        👁️
                      </button>
                      <button className="supplierdashboard-action-btn supplierdashboard-more-btn">
                        ⋯
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Appointments */}
            <div className="supplierdashboard-content-card">
              <div className="supplierdashboard-card-header">
                <div className="supplierdashboard-card-title">
                  <div className="supplierdashboard-card-icon supplierdashboard-appointment-icon">
                    📅
                  </div>
                  <div>
                    <h3>Upcoming Appointments</h3>
                    <p>Scheduled visa appointments</p>
                  </div>
                </div>
                <button className="supplierdashboard-view-all-btn">
                  View All →
                </button>
              </div>

              <div className="supplierdashboard-card-content">
                {appointments.map((apt, index) => (
                  <div
                    key={index}
                    className="supplierdashboard-appointment-row"
                  >
                    <div className="supplierdashboard-appointment-content">
                      <div className="supplierdashboard-appointment-icon">
                        <div className="supplierdashboard-icon-container">
                          <span className="supplierdashboard-time-icon">
                            ⏰
                          </span>
                          {apt.status === "completed" && (
                            <span className="supplierdashboard-completed-icon">
                              ✓
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="supplierdashboard-appointment-details">
                        <h4>{apt.applicant}</h4>
                        <div className="supplierdashboard-appointment-time">
                          <span>{apt.date}</span>
                          <span className="supplierdashboard-time-separator">
                            •
                          </span>
                          <span className="supplierdashboard-time-value">
                            {apt.time}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="supplierdashboard-appointment-info">
                      <span
                        className={`supplierdashboard-appointment-type ${getStatusColor(
                          apt.type
                        )}`}
                      >
                        {apt.type.charAt(0).toUpperCase() + apt.type.slice(1)}
                      </span>
                      <span className="supplierdashboard-appointment-id">
                        {apt.id}
                      </span>
                    </div>

                    <div className="supplierdashboard-appointment-actions">
                      <button className="supplierdashboard-action-btn-small supplierdashboard-reschedule-btn">
                        Reschedule
                      </button>
                      <button className="supplierdashboard-action-btn-small supplierdashboard-details-btn">
                        Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Promotions */}
          <div className="supplierdashboard-content-card">
            <div className="supplierdashboard-card-header">
              <div className="supplierdashboard-card-title">
                <div className="supplierdashboard-card-icon supplierdashboard-promotion-icon">
                  📢
                </div>
                <div>
                  <h3>Active Promotions</h3>
                  <p>Running promotional campaigns</p>
                </div>
              </div>
              <button className="supplierdashboard-view-all-btn">
                Create New →
              </button>
            </div>

            <div className="supplierdashboard-promotions-list">
              {promotions.map((promo, index) => (
                <div key={index} className="supplierdashboard-promotion-card">
                  <div className="supplierdashboard-promotion-header">
                    <div className="supplierdashboard-promotion-content">
                      <div className="supplierdashboard-promotion-icon">
                        <div className="supplierdashboard-icon-container">
                          <span className="supplierdashboard-trend-icon">
                            📈
                          </span>
                          {promo.status === "active" && (
                            <div className="supplierdashboard-active-dot" />
                          )}
                        </div>
                      </div>
                      <div className="supplierdashboard-promotion-details">
                        <h4>{promo.name}</h4>
                        <div className="supplierdashboard-promotion-discount">
                          <span className="supplierdashboard-discount-value">
                            {promo.discount}
                          </span>
                          <span
                            className={`supplierdashboard-promotion-status ${
                              promo.status === "active"
                                ? "supplierdashboard-status-active"
                                : "supplierdashboard-status-expired"
                            }`}
                          >
                            {promo.status === "active" ? "Active" : "Expired"}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="supplierdashboard-promotion-date">
                      <div className="supplierdashboard-date-info">
                        <span className="supplierdashboard-calendar-icon">
                          📅
                        </span>
                        <span>
                          {promo.validFrom} - {promo.validTo}
                        </span>
                      </div>
                      <div className="supplierdashboard-usage-info">
                        {promo.usage}
                      </div>
                    </div>
                  </div>

                  <div className="supplierdashboard-promotion-progress">
                    <div className="supplierdashboard-progress-info">
                      <span>Usage progress</span>
                      <span>{promo.usage}</span>
                    </div>
                    <div className="supplierdashboard-progress-bar-container">
                      <div
                        className={`supplierdashboard-progress-bar-fill ${
                          promo.status === "active"
                            ? "supplierdashboard-progress-active"
                            : "supplierdashboard-progress-expired"
                        }`}
                        style={{
                          width: `${
                            (parseInt(promo.usage.split("/")[0]) /
                              parseInt(promo.usage.split("/")[1])) *
                            100
                          }%`,
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SupplierDashboard;
